import { GAME_ASSET_SLUG } from "@/config/game";
import { cn } from "@/lib/utils";
import {
  FavoriteType,
  favoritesFlowSelector,
  setPickerType,
} from "@/redux/frontPage/frontPageSlice";
import { loadedContentSelector } from "@/redux/loadedContent/loadedContentSlice";
import { useTranslation } from "@repo/shared/i18n";
import { Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

/*
  Two-step indicator. Step order follows `firstChosen` (reversible) — clicking a
  step jumps the picker to that type, letting the user pick Country first. A step
  is "active" when it's the one being edited, "completed" when it holds a
  selection but isn't active, otherwise "upcoming".
*/
export const FavoriteStepper = () => {
  const { t } = useTranslation("frontPage");
  const dispatch = useDispatch();
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const { pickerType, firstChosen, selectedClubIds, selectedCountryId } =
    useSelector(favoritesFlowSelector);
  const { clubs, countries } = useSelector(loadedContentSelector);

  const order: FavoriteType[] =
    firstChosen === "country" ? ["country", "club"] : ["club", "country"];

  const hasSelection = (type: FavoriteType) =>
    type === "club" ? selectedClubIds.length > 0 : selectedCountryId != null;

  const chosenLabel = (type: FavoriteType) => {
    if (type === "club") {
      const first = clubs?.find((c) => c.id === selectedClubIds[0]);
      const extra = selectedClubIds.length - 1;
      return (
        (first?.name ?? t("favorites.stepper.clubFallback")) +
        (extra > 0 ? ` +${extra}` : "")
      );
    }
    return (
      countries?.find((c) => c.id === selectedCountryId)?.name ??
      t("favorites.stepper.countryFallback")
    );
  };

  const chosenImg = (type: FavoriteType) =>
    type === "club"
      ? `${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${selectedClubIds[0]}.png?scale=50`
      : `${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${selectedCountryId}.png?scale=50`;

  return (
    <div className="flex items-center gap-2.5">
      {order.map((type, index) => {
        const active = pickerType === type;
        const completed = !active && hasSelection(type);
        const stepNo = index + 1;
        return (
          <div key={type} className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => dispatch(setPickerType(type))}
              className="flex items-center gap-2"
            >
              <span
                className={cn(
                  "flex size-[22px] items-center justify-center rounded-full text-xs font-bold",
                  active && "bg-blue-500 text-white",
                  completed && "bg-emerald-600 text-white",
                  !active && !completed && "bg-neutral-800 text-neutral-400",
                )}
              >
                {completed ? <Check size={13} strokeWidth={3} /> : stepNo}
              </span>

              {completed ? (
                <span className="flex items-center gap-1.5">
                  <img
                    src={chosenImg(type)}
                    alt=""
                    className="size-[18px] object-contain"
                  />
                  <span className="text-sm text-neutral-300">
                    {chosenLabel(type)}
                  </span>
                </span>
              ) : (
                <span
                  className={cn(
                    "text-sm",
                    active
                      ? "font-medium text-neutral-100"
                      : "text-neutral-400",
                  )}
                >
                  {type === "club"
                    ? t("favorites.stepper.club")
                    : t("favorites.stepper.country")}
                </span>
              )}
            </button>

            {index === 0 && <span className="h-px w-7 bg-neutral-700" />}
          </div>
        );
      })}
    </div>
  );
};
